import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { format } from 'date-fns';
import TextField from './Shared/TextField';
import CheckboxGroup from './Shared/CheckboxGroup';
import MatrixTextboxes from './Shared/MatrixTextboxes';
import FormFillCanvas, { FormFillCanvasHandle } from './Shared/FormFillCanvas';
import SectionCard from './Shared/SectionCard';
import ReportDetailsCard from './Shared/ReportDetailsCard';
import PDFReportPreview from './PreviewTool/PDFReportPreview';
import TabsBar from './PreviewTool/TabsBar';
import { RootState } from '../../redux/root';
import { dispatch } from '../../redux/store';
import { setSelectedInspection } from '../../redux/slices/inspectionsSlice';
import { SIGNATURE } from './PreviewTool/thresholdConstants';
import Loader from '../../atoms/Loader';
import useFormMarkupTools from './useFormMarkupTools';
import useSelectedFormImages from './useSelectedFormImages';
import {
  buildFieldBoxes,
  computeFitWidths,
  countProgress,
  applyStatusToResult,
  findMissingForReport,
  groupIntoSections,
  hasStatusDrivenResult,
  isFieldFilled,
  isStatusDrivenResult,
  refKey,
  sortReadingOrder,
  splitRefKey,
} from './fillLayout';

interface Props {
  mainTab: number;
  // The status the server holds for this inspection — NOT the one picked in
  // the editor. Autosave writes the status-driven Result as this status has
  // it; the picked status reaches the server only with Update.
  savedStatusName?: string;
}

// How long to wait after the last change before pushing it to the server.
const SAVE_DEBOUNCE_MS = 1200;

// Below this viewport width, typing on the page is too fiddly — fall back to the
// section-grouped field list.
const CANVAS_MIN_WIDTH = 1000;

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 3;
const ZOOM_INCREMENT = 0.25;

const FillThresholdReport = ({ mainTab, savedStatusName }: Props) => {
  const [showControls, setShowControls] = useState<boolean>(true);
  const [tab, setTab] = useState<string>('0');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Multiplier on top of fit-to-width. 1 = the page exactly fills the panel.
  const [zoomStep, setZoomStep] = useState<number>(1);
  // Direct-on-PDF needs room. Below this the section-grouped field list takes
  // over. Keyed off the measured window, NOT hooks/useDevice (which only
  // user-agent-sniffs for iPad — an iPad in landscape has plenty of width and a
  // narrow laptop window does not).
  const [isNarrow, setIsNarrow] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < CANVAS_MIN_WIDTH,
  );
  useEffect(() => {
    const update = () => setIsNarrow(window.innerWidth < CANVAS_MIN_WIDTH);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const { selectedInspection } = useSelector((s: RootState) => s.inspections);
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);
  const { updateFormMarkupOnInspection, saveState, lastSavedAt } = useFormMarkupTools();
  // Page images are fetched separately from the form list; see the hook.
  const { imagesLoading } = useSelectedFormImages();

  // Editing writes back through selectedInspection.formMarkup, so fields may
  // only be rendered once the inspection actually has its own markup. The form
  // template is a read-only fallback used for laying the page out before then —
  // rendering inputs from it would crash on the first keystroke.
  const editableMarkup = selectedInspection?.formMarkup;
  const markup = editableMarkup || (selectedForm ? selectedForm.formMarkup : {});
  const page = (markup && markup[tab]) || {};

  // Geometry only changes when a different report is loaded (switching the
  // inspection type swaps selectedForm AND formMarkup) or the page changes —
  // never while typing. Key the memo on the field set so keystrokes don't
  // re-derive the whole layout.
  // Page keys come from the form's page IMAGES, not the markup: a form can have
  // more scanned pages than pages carrying fields.
  const pageImages: Array<{ base64: string }> = selectedForm?.fileBase64 || [];
  // Built from the PAGE entries only. The markup also carries flat, non-page
  // metadata at the top level (`completed`, written by the former Completed toggle),
  // and folding that in churned the signature on changes that cannot affect
  // any layout.
  const markupSignature = pageImages
    .map((_img, index) => `${index}:${Object.keys((markup && markup[`${index}`]) || {}).join(',')}`)
    .join('|');

  const layouts = useMemo(() => {
    const out: Record<string, any> = {};
    pageImages.forEach((_img, index) => {
      const pageKey = `${index}`;
      const pageData = (markup && markup[pageKey]) || {};
      const ordered = sortReadingOrder(buildFieldBoxes(markup, pageKey));
      const isHidden = (key: string) =>
        key === SIGNATURE || !!(pageData[key] && pageData[key].isStatic);
      out[pageKey] = {
        boxes: ordered,
        // Usable width per field, so on-page inputs stop short of their
        // neighbours instead of running to the page edge and overlapping.
        fitWidths: computeFitWidths(ordered),
        sections: groupIntoSections(ordered)
          .map(section => ({ ...section, keys: section.keys.filter(k => !isHidden(k)) }))
          .filter(section => section.keys.length > 0),
        staticKeys: ordered.filter(b => b.isStatic).map(b => b.key),
        // The Results row follows Activity Status and cannot be ticked here, so
        // Enter-advance / "Next empty" step over it.
        editableKeys: ordered
          .map(b => b.key)
          .filter(k => !isHidden(k) && !isStatusDrivenResult(k, pageData[k])),
      };
    });
    return out;
    // eslint-disable-next-line
  }, [markupSignature, pageImages.length, selectedForm]);

  // The current page's layout still drives the small-screen field list.
  const layout = layouts[tab] || {
    boxes: [],
    fitWidths: {},
    sections: [],
    staticKeys: [],
    editableKeys: [],
  };

  const fitWidthsByPage = useMemo(
    () =>
      Object.keys(layouts).reduce(
        (acc: Record<string, any>, p) => ({ ...acc, [p]: layouts[p].fitWidths }),
        {},
      ),
    [layouts],
  );

  // Enter-advance and "next empty" walk every page in order, so they carry on
  // into the next page instead of stopping at the bottom of the current one.
  const allEditableKeys = useMemo(
    () =>
      Object.keys(layouts)
        .sort((a, b) => Number(a) - Number(b))
        .reduce((acc: string[], p) => {
          const keys = layouts[p].editableKeys.map((k: string) => refKey(p, k));
          return acc.concat(keys);
        }, []),
    [layouts],
  );

  // Progress spans the whole form now that every page is on screen at once.
  const progress = Object.keys(layouts).reduce(
    (acc, p) => {
      const pageCount = countProgress(markup, p, Object.keys((markup && markup[p]) || {}));
      return { filled: acc.filled + pageCount.filled, total: acc.total + pageCount.total };
    },
    { filled: 0, total: 0 },
  );

  // Focus registry, in reading order, powering "next empty" and Enter-to-advance.
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  // Stable identity so React isn't detaching/reattaching every field's ref on
  // each keystroke re-render.
  const registerRef = useCallback((key: string, el: HTMLElement | null) => {
    fieldRefs.current[key] = el;
  }, []);

  const focusKey = (key: string) => {
    const el = fieldRefs.current[key];
    if (!el) return;
    el.focus();
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    setActiveKey(key);
  };

  const resultFromStatus = hasStatusDrivenResult(editableMarkup);

  // Replaces the old "Completed" tick: the form is ready for Create Report the
  // moment nothing required is blank. Pdf.tsx refuses on the same list.
  const missingForReport = useMemo(() => findMissingForReport(editableMarkup), [editableMarkup]);

  const firstEmptyKey = () =>
    allEditableKeys.find(composite => {
      const { page: p, key } = splitRefKey(composite);
      return !isFieldFilled(((markup && markup[p]) || {})[key]);
    });

  const jumpToNextEmpty = () => {
    const key = firstEmptyKey();
    if (key) focusKey(key);
  };

  const advanceFrom = (key: string) => {
    const index = allEditableKeys.indexOf(key);
    const next = allEditableKeys[index + 1];
    if (next) focusKey(next);
  };

  const retreatFrom = (key: string) => {
    const index = allEditableKeys.indexOf(key);
    const prev = allEditableKeys[index - 1];
    if (prev) focusKey(prev);
  };

  // Step through fields in reading order from wherever you are, crossing page
  // boundaries — focusKey scrolls the target into view, so the page follows.
  const activeIndex = activeKey ? allEditableKeys.indexOf(activeKey) : -1;
  const canStepBack = activeIndex > 0;
  const canStepForward = activeIndex > -1
    ? activeIndex < allEditableKeys.length - 1
    : allEditableKeys.length > 0;

  const stepField = (delta: number) => {
    if (activeIndex < 0) {
      // Nothing selected yet: enter the form at whichever end you asked for.
      const entry = delta > 0 ? allEditableKeys[0] : allEditableKeys[allEditableKeys.length - 1];
      if (entry) focusKey(entry);
      return;
    }
    if (delta > 0) advanceFrom(activeKey as string);
    else retreatFrom(activeKey as string);
  };

  // --- autosave -------------------------------------------------------------
  // The form persists on its own; the Update button is only needed for the
  // inspection details above it. Every change is pushed after a short pause, immediately on
  // blur, and once more when the tab unmounts so the last edit is never lost.
  // Always points at the newest markup, so handlers that commit and save in one
  // go never fall back to a stale copy from their own render.
  const markupRef = useRef<Record<string, any> | undefined>(editableMarkup);
  markupRef.current = editableMarkup;

  const pendingRef = useRef<Record<string, any> | null>(null);
  const timerRef = useRef<any>(null);
  const lastSeenRef = useRef<Record<string, any> | undefined>(undefined);
  // The markup the last PUT carried. Leaving a field flushes unconditionally,
  // so without this, tabbing across a row of fields — or clicking any control
  // while one has focus — fired a PUT per blur with byte-identical content.
  const lastSavedRef = useRef<Record<string, any> | undefined>(undefined);

  // Refs so the unmount handler and the debounce timer always reach the current
  // save function instead of a stale closure.
  const saveRef = useRef(updateFormMarkupOnInspection);
  saveRef.current = updateFormMarkupOnInspection;
  const savedStatusRef = useRef(savedStatusName);
  savedStatusRef.current = savedStatusName;

  // `force` is for the retry button: after a failed save the markup is still the
  // one we last tried to send, and the identity check alone would swallow it.
  const flushSave = (force = false) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (!force && pending === lastSavedRef.current) return;
    lastSavedRef.current = pending;
    // On screen the Result follows the status picked in the editor, but that
    // status is unsaved until Update. Autosave used to send the picked
    // Result with every keystroke, so the stored form said Pass while the
    // stored inspection still said Scheduled. Send it as the SAVED status has
    // it; Update sends the picked one together with its status.
    const status = savedStatusRef.current;
    saveRef.current(status ? applyStatusToResult(pending, status) : pending);
  };
  const flushRef = useRef(flushSave);
  flushRef.current = flushSave;

  // True once the INSPECTOR has changed something in this tab. Set only by the
  // edit handlers below, never by a dispatch that came from loading.
  //
  // Opening an inspection replaces the markup twice without anyone typing:
  // InspectionTabs rebases the form template onto it, then
  // useLoadInspectionEditor dispatches the authoritative copy from the server.
  // The watcher could not tell those from an edit, so each one scheduled a PUT
  // — two on every open, a network round-trip apart, so the debounce did not
  // collapse them. That is how a load-time markup got written back over the
  // server's good copy, turning an answer the rebase had dropped into a
  // permanent loss. Loading must never trigger a save.
  const userEditedRef = useRef(false);

  // One place watches the markup, so no field component has to remember to
  // persist — typing, ticking a box and editing a matrix cell all land here.
  useEffect(() => {
    if (!editableMarkup) return;
    if (!userEditedRef.current) {
      // Arrived from loading: adopt it as the baseline to compare edits
      // against, and do not push it back. Also stops a blur with no edit from
      // firing a PUT with byte-identical content.
      lastSeenRef.current = editableMarkup;
      lastSavedRef.current = editableMarkup;
      return;
    }
    if (lastSeenRef.current === editableMarkup) return;
    lastSeenRef.current = editableMarkup;
    pendingRef.current = editableMarkup;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current(), SAVE_DEBOUNCE_MS);
  }, [editableMarkup]);

  useEffect(() => () => flushRef.current(), []);

  // Every page is stacked in one scroll area, so `tab` is now "the page in
  // view" rather than "the page rendered": clicking a Page pill scrolls to it,
  // and scrolling updates which pill is lit.
  const canvasRef = useRef<FormFillCanvasHandle | null>(null);

  const goToPage = (index: string) => {
    setTab(index);
    if (canvasRef.current) canvasRef.current.scrollToPage(Number(index));
  };

  const onVisiblePage = (index: number) => {
    const next = `${index}`;
    setTab(prev => (prev === next ? prev : next));
  };

  // Straight to the first blank required field, on whatever page it sits.
  // The field list (narrow screens) only renders the current page, so the
  // focus waits a beat for that page to mount.
  const jumpToMissing = () => {
    const first = missingForReport[0];
    if (!first) return;
    goToPage(first.page);
    setTimeout(() => focusKey(refKey(first.page, first.key)), 60);
  };

  const retrySave = () => {
    if (!editableMarkup) return;
    pendingRef.current = editableMarkup;
    flushSave(true);
  };

  useEffect(() => {
    if (mainTab === 0) {
      setShowControls(true);
    }
  }, [mainTab]);

  // Deliberately NO auto-focus on open or on page change.
  //
  // It used to jump to the first unfilled field, which calls focus() +
  // scrollIntoView() and therefore moves the scroll position out from under the
  // inspector — on a page whose first gap is near the bottom that reads as the
  // view leaping somewhere they did not ask to go. "Next empty" does the same
  // thing on request, which is where that behaviour belongs.

  const onChange = e => {
    userEditedRef.current = true;
    const { name, value } = e.target;
    const nextMarkup = {
      ...selectedInspection.formMarkup,
      [tab]: {
        ...selectedInspection.formMarkup[tab],
        [name]: {
          ...selectedInspection.formMarkup[tab][name],
          text: value,
        },
      },
    };
    dispatch(setSelectedInspection({ ...selectedInspection, formMarkup: nextMarkup }));
  };

  // Only the canvas and the field-list controls get this — every caller is an
  // inspector edit. The load paths dispatch setSelectedInspection directly.
  const setData = arg => {
    userEditedRef.current = true;
    dispatch(setSelectedInspection({ ...selectedInspection, formMarkup: arg }));
  };

  const onBlur = (updatedMarkup = null) => {
    // Callers may pass an event (React handlers) or the markup they just built.
    const isEvent = updatedMarkup?.nativeEvent !== undefined;
    const explicit = updatedMarkup && !isEvent ? updatedMarkup : null;

    if (explicit) {
      // Markup built by a handler (checkbox toggle, the Completed switch) —
      // an edit, so the watcher is allowed to save from here on.
      userEditedRef.current = true;
      dispatch(setSelectedInspection({ ...selectedInspection, formMarkup: explicit }));
    }

    // With no explicit markup, whatever is in the store is already current — so
    // read the latest through a ref and do NOT re-dispatch. Re-dispatching a
    // copy captured by this render would revert any change made in the same
    // handler, which is exactly what silently undid radio/checkbox clicks.
    const markupToSave = explicit || markupRef.current;

    // Leaving a field is a natural commit point — save straight away rather
    // than waiting out the debounce.
    pendingRef.current = markupToSave;
    lastSeenRef.current = markupToSave;
    flushSave();
  };

  const hasImage = pageImages.length > 0 && !!pageImages[0].base64;
  // Direct-on-PDF needs both a page image and room to render it.
  const useCanvas = hasImage && !isNarrow;

  // The Notes tab has real content to show only once the form data has arrived
  // AND, for the canvas path, the page images have decoded and painted. Until
  // then keep one spinner up — the download (several MB) and the decode are both
  // seconds long, and the tab is mounted-but-hidden the whole time it happens,
  // so nothing rendered a spinner before the canvas even existed.
  //
  // Mirrored straight from the canvas, NOT latched here. This used to be re-armed
  // by an effect keyed on `markupSignature`, which is a set-of-keys signature —
  // so ticking "Completed" (a top-level `completed` key on the markup) re-armed
  // the spinner while the already-mounted canvas stayed ready and had nothing
  // left to report. The spinner then stayed up forever over a fully loaded form.
  // Switching inspection type always dispatches a new `selectedForm`, and the
  // canvas re-reports `false` while the new page images decode, so the genuine
  // wait is still covered.
  const [canvasReady, setCanvasReady] = useState(false);
  const onCanvasReadyChange = useCallback((ready: boolean) => setCanvasReady(ready), []);

  // `imagesLoading` covers the window where selectedForm is assigned but its
  // page images have not arrived yet — without it the canvas would render a
  // fieldless blank page and look finished.
  const notesLoading =
    !selectedForm || imagesLoading || (!!editableMarkup && useCanvas && !canvasReady);

  const renderField = (key: string) => {
    const item = page[key];
    if (item === undefined) return null;

    // The field-list components know nothing about pages, so translate their
    // plain field names into the same `page::field` keys the canvas registers —
    // otherwise "next empty" and Enter-advance would look up the wrong entries.
    const composite = refKey(tab, key);
    const wiring = {
      registerRef: (_k: string, el: HTMLElement | null) => registerRef(composite, el),
      onFocusField: () => setActiveKey(composite),
      onAdvance: () => advanceFrom(composite),
    };

    if (item.inputType) {
      return (
        <CheckboxGroup
          key={key}
          item={item}
          keyName={key}
          tab={tab}
          data={selectedInspection.formMarkup}
          setData={setData}
          onBlur={onBlur}
          locked={isStatusDrivenResult(key, item)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...wiring}
        />
      );
    }

    if (item.matrix) {
      return (
        <MatrixTextboxes
          key={key}
          item={item}
          keyName={key}
          tab={tab}
          data={selectedInspection.formMarkup}
          setData={setData}
          onUpperBlur={onBlur}
          registerRef={registerRef}
          onFocusField={setActiveKey}
        />
      );
    }

    return (
      <TextField
        key={key}
        onChange={onChange}
        name={key}
        item={item}
        value={item.text}
        onBlur={onBlur}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...wiring}
      />
    );
  };

  return (
    <Root>
      <HeaderBar>
        {showControls ? (
          <TabsBar tab={tab} setTab={goToPage} />
        ) : (
          <PreviewTitle>Report Preview</PreviewTitle>
        )}

        {showControls && !notesLoading && (
          <Tools>
            <ProgressChip $done={progress.total > 0 && progress.filled === progress.total}>
              {progress.filled} of {progress.total} filled
            </ProgressChip>

            {missingForReport.length === 0 ? (
              <ProgressChip $done title="Every required field is filled">
                <CheckIcon style={{ fontSize: 14, marginRight: 4 }} />
                Ready for report
              </ProgressChip>
            ) : (
              <SaveError
                type="button"
                onClick={jumpToMissing}
                title={missingForReport.map(m => `${m.label} (page ${m.pageNumber})`).join(', ')}
              >
                <ErrorOutlineIcon style={{ fontSize: 15 }} />
                <span>
                  {missingForReport.length} required{' '}
                  {missingForReport.length === 1 ? 'field' : 'fields'} missing
                </span>
              </SaveError>
            )}

            {saveState === 'error' ? (
              <SaveError type="button" onClick={retrySave}>
                <ErrorOutlineIcon style={{ fontSize: 15 }} />
                <span>Not saved — retry</span>
              </SaveError>
            ) : (
              <SaveChip $saved={saveState === 'saved'}>
                {saveState === 'saving' && 'Saving…'}
                {saveState === 'saved' && lastSavedAt && `Saved ${format(lastSavedAt, 'h:mm a')}`}
                {saveState === 'idle' && 'Saves automatically'}
              </SaveChip>
            )}
            <SmallButton type="button" onClick={jumpToNextEmpty} disabled={!firstEmptyKey()}>
              <ArrowDownwardIcon style={{ fontSize: 16 }} />
              <span>Next empty</span>
            </SmallButton>
            {useCanvas && (
              <ZoomGroup>
                <SmallButton
                  type="button"
                  title="Zoom out"
                  onClick={() => setZoomStep(z => Math.max(ZOOM_MIN, z - ZOOM_INCREMENT))}
                  disabled={zoomStep <= ZOOM_MIN}
                >
                  <RemoveIcon style={{ fontSize: 16 }} />
                </SmallButton>
                <SmallButton
                  type="button"
                  title="Fit the page to the panel"
                  onClick={() => setZoomStep(1)}
                  $active={zoomStep === 1}
                >
                  <span>{Math.round(zoomStep * 100)}%</span>
                </SmallButton>
                <SmallButton
                  type="button"
                  title="Zoom in"
                  onClick={() => setZoomStep(z => Math.min(ZOOM_MAX, z + ZOOM_INCREMENT))}
                  disabled={zoomStep >= ZOOM_MAX}
                >
                  <AddIcon style={{ fontSize: 16 }} />
                </SmallButton>
              </ZoomGroup>
            )}

            {useCanvas && (
              <StepGroup>
                <SmallButton
                  type="button"
                  title="Previous field (Alt + Up, or Shift + Enter)"
                  onClick={() => stepField(-1)}
                  disabled={!canStepBack}
                >
                  <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                </SmallButton>
                <SmallButton
                  type="button"
                  title="Next field (Alt + Down, or Enter)"
                  onClick={() => stepField(1)}
                  disabled={!canStepForward}
                >
                  <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                </SmallButton>
              </StepGroup>
            )}
          </Tools>
        )}

        <ToggleButton type="button" onClick={() => setShowControls(prev => !prev)}>
          {showControls ? (
            <>
              <VisibilityIcon style={{ fontSize: 18 }} />
              <span>Report (Preview)</span>
            </>
          ) : (
            <>
              <EditIcon style={{ fontSize: 18 }} />
              <span>Edit Form</span>
            </>
          )}
        </ToggleButton>
      </HeaderBar>

      {/* The form persists on its own, but Update is the modal's only submit —
          it is what saves the inspection details above and runs the completion
          flow (report attachment + client SMS) when the status changed. Without
          this line, an inspector who has seen "Saved" here reasonably assumes
          Update is redundant and loses their details edits. */}
      {showControls && !notesLoading && (
        <Hint>
          <InfoOutlinedIcon style={{ fontSize: 16 }} />
          <span>
            The inspection form saves automatically.
            {resultFromStatus && (
              <>
                {' '}Its result is set from <strong>Activity Status</strong> above.
              </>
            )}
            {' '}Use <strong>Update</strong> to save the inspection details and finish the inspection.
          </span>
        </Hint>
      )}

      {/* One content region. The canvas (or field list) mounts as soon as the
          data is here so it can measure and decode, and a single spinner sits on
          top of that same box until real content has painted — covering the
          download AND the render, both of which are seconds long and were
          previously an uncovered blank. */}
      {showControls && (
        <ContentArea>
          {/* Direct-on-PDF: every control sits where its value will print. */}
          {useCanvas && editableMarkup && selectedForm && (
            <FormFillCanvas
              ref={canvasRef}
              pages={pageImages}
              data={selectedInspection.formMarkup}
              setData={setData}
              onBlur={onBlur}
              fitWidthsByPage={fitWidthsByPage}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              registerRef={registerRef}
              onAdvance={advanceFrom}
              onRetreat={retreatFrom}
              zoomStep={zoomStep}
              onVisiblePage={onVisiblePage}
              onReadyChange={onCanvasReadyChange}
            />
          )}

          {/* Fallback for narrow viewports, and for forms whose page image is
              missing — typing on a page you cannot see is not an option. */}
          {!notesLoading && (!useCanvas || !editableMarkup) && (
            <Panes>
              <RightPane>
                {editableMarkup && <ReportDetailsCard page={page} keys={layout.staticKeys} />}

                {editableMarkup && layout.sections.map((section, index) => {
                  const sectionProgress = countProgress(markup, tab, section.keys);
                  return (
                    <SectionCard
                      key={section.id}
                      index={index}
                      total={layout.sections.length}
                      filled={sectionProgress.filled}
                      fieldCount={sectionProgress.total}
                    >
                      {section.keys.map(renderField)}
                    </SectionCard>
                  );
                })}

                {(!editableMarkup || !layout.sections.length) && (
                  <Empty>This report has no fields to fill on this page.</Empty>
                )}
              </RightPane>
            </Panes>
          )}

          {notesLoading && (
            <LoadingOverlay>
              <Loader />
              <span>Loading report…</span>
            </LoadingOverlay>
          )}
        </ContentArea>
      )}

      {!showControls && (
        <PreviewWrapper>
          <PDFReportPreview data={selectedInspection.formMarkup} />
        </PreviewWrapper>
      )}
    </Root>
  );
};

export default FillThresholdReport;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 24px 24px;
  font-family: ${FONT_STACK};
  box-sizing: border-box;
  width: 100%;
  min-width: 0;

  @media only screen and (max-width: 840px) {
    padding: 14px;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px;
    gap: 10px;
  }
`;

// Pinned to the top of the tab's scroll area. The form page is far taller than
// the viewport, so scrolling down used to take the page tabs, zoom and
// Report (Preview) with it — exactly the controls you need while working down
// a long form. Negative margins bleed it back out to the Root padding so the
// content scrolls fully underneath rather than beside it.
const HeaderBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--neutral-0);
  border-bottom: 1px solid var(--border);
  margin: -20px -24px 0;
  padding: 14px 24px;

  @media only screen and (max-width: 840px) {
    margin: -14px -14px 0;
    padding: 12px 14px;
  }

  @media only screen and (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }

  @media only screen and (max-width: 540px) {
    margin: -10px -10px 0;
    padding: 10px;
  }
`;

const Tools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;

  @media only screen and (max-width: 720px) {
    margin-left: 0;
  }
`;

const ProgressChip = styled.span<{ $done: boolean }>`
  flex: 0 0 auto;
  height: 28px;
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid ${({ $done }) => ($done ? 'var(--success-100)' : 'var(--border)')};
  background: ${({ $done }) => ($done ? 'var(--success-50)' : 'var(--neutral-50)')};
  color: ${({ $done }) => ($done ? 'var(--success-700)' : 'var(--neutral-600)')};
`;

const SaveChip = styled.span<{ $saved: boolean }>`
  flex: 0 0 auto;
  height: 28px;
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  color: ${({ $saved }) => ($saved ? 'var(--success-700)' : 'var(--neutral-500)')};
`;

const SaveError = styled.button`
  flex: 0 0 auto;
  height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--danger-500);
  background: var(--danger-50);
  color: var(--danger-500);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SmallButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--primary-50)' : 'var(--neutral-0)')};
  color: ${({ $active }) => ($active ? 'var(--primary-700)' : 'var(--neutral-700)')};

  &:hover:not(:disabled) {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const PreviewTitle = styled.div`
  flex: 1;
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  letter-spacing: 0.01em;
`;

// Filled primary, matching the modal's Update button (InspectionEditor's
// PrimaryButton) — the two sit in the same view and read as one toolbar.
const ToggleButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    color: currentColor;
  }
`;

const Hint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: -4px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--neutral-500);

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--neutral-400);
  }

  strong {
    font-weight: 600;
    color: var(--neutral-700);
  }
`;

// Wrapper for the small-screen field-list fallback.
const Panes = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  width: 100%;
  min-width: 0;
`;

const ZoomGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  /* The percentage readout doubles as the "fit" button, so keep it wide enough
     that the label does not jump around as the number changes. */
  button:nth-child(2) {
    min-width: 56px;
    justify-content: center;
  }
`;

// Field stepper, sat beside the zoom controls.
const StepGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  button {
    min-width: 30px;
    justify-content: center;
    padding: 0 6px;
  }
`;

const RightPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

// The content region. Its height comes from whatever is inside (the canvas's
// own bounded Scroll box, or the field list), and the loading overlay pins to
// it — a min-height keeps the spinner centred in a sensible area even before
// any content has mounted.
const ContentArea = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 320px;
`;

// Opaque cover over the content region while it loads, so the inspector sees a
// spinner instead of a blank panel. Sticky-topped so it stays in view even if
// the (hidden) content underneath is taller than the viewport.
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  max-height: calc(70vh - 160px);
  background: var(--neutral-0);
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const PreviewWrapper = styled.div`
  width: 100%;

  iframe {
    width: 100%;
    min-height: 500px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--neutral-50);
  }
`;
